import React, {memo} from "react";

/**
 * Props comparison function for chart components
 * Only re-render if essential chart data has changed
 */
const chartPropsAreEqual = (prevProps: any, nextProps: any): boolean => {
  // Always re-render if axisSeries reference changed (new activity selected)
  if (prevProps.axisSeries !== nextProps.axisSeries) {
    return false;
  }

  // Check if data points have actually changed
  if (prevProps.points !== nextProps.points) {
    return false;
  }

  // Check if title changed
  if (prevProps.title !== nextProps.title) {
    return false;
  }

  // Check if color changed
  if (prevProps.color !== nextProps.color) {
    return false;
  }

  // For most other props, assume they don't change frequently
  return true;
};

/**
 * Higher-order component that memoizes chart components
 * Prevents unnecessary re-renders when props haven't meaningfully changed
 */
export const withChartMemoization = <P extends object>(
  Component: React.ComponentType<P>,
  customPropsComparison?: (prevProps: P, nextProps: P) => boolean
) => {
  const MemoizedComponent = memo(Component, customPropsComparison || chartPropsAreEqual);
  MemoizedComponent.displayName = `Memoized(${Component.displayName || Component.name})`;
  return MemoizedComponent;
};

/**
 * Specific memoization for GenericValueBarChartReport components
 * These are the most common and expensive chart components
 */
export const memoizedGenericChartPropsAreEqual = (prevProps: any, nextProps: any): boolean => {
  // Critical props that should trigger re-render
  const criticalProps = ["axisSeries", "points", "title", "color", "dataSeriesKey", "seriesName"];

  for (const prop of criticalProps) {
    if (prevProps[prop] !== nextProps[prop]) {
      return false;
    }
  }

  // IMPORTANT: Check if zoom state has changed - needed for synchronized zooming
  if (prevProps.zoomState !== nextProps.zoomState) {
    // Deep check for zoom state changes since it's an object
    if (!prevProps.zoomState && !nextProps.zoomState) {
      // Both undefined/null, no change
    } else if (!prevProps.zoomState || !nextProps.zoomState) {
      // One is undefined/null, the other isn't - changed
      return false;
    } else if (
      prevProps.zoomState.min !== nextProps.zoomState.min ||
      prevProps.zoomState.max !== nextProps.zoomState.max
    ) {
      // Zoom values changed
      return false;
    }
  }

  // Check if callbacks changed (they shouldn't normally, but just in case)
  if (prevProps.onZoomChange !== nextProps.onZoomChange || prevProps.onResetZoom !== nextProps.onResetZoom) {
    return false;
  }

  return true;
};

/**
 * Memoization for FPS and UDP reports which are rendered together
 * and often have correlated data
 */
export const memoizedPerformanceChartPropsAreEqual = (prevProps: any, nextProps: any): boolean => {
  // These charts are in the "Performance & Network Correlation" section
  // They should re-render together when axisSeries changes
  if (prevProps.axisSeries !== nextProps.axisSeries) {
    return false;
  }

  // IMPORTANT: Check if zoom state has changed - needed for synchronized zooming
  if (prevProps.zoomState !== nextProps.zoomState) {
    // Deep check for zoom state changes since it's an object
    if (!prevProps.zoomState && !nextProps.zoomState) {
      // Both undefined/null, no change
    } else if (!prevProps.zoomState || !nextProps.zoomState) {
      // One is undefined/null, the other isn't - changed
      return false;
    } else if (
      prevProps.zoomState.min !== nextProps.zoomState.min ||
      prevProps.zoomState.max !== nextProps.zoomState.max
    ) {
      // Zoom values changed
      return false;
    }
  }

  // Check if callbacks changed (they shouldn't normally, but just in case)
  if (prevProps.onZoomChange !== nextProps.onZoomChange || prevProps.onResetZoom !== nextProps.onResetZoom) {
    return false;
  }

  // Check specific data props
  const dataProps = ["points", "min", "max", "avg", "stdev", "samples"];
  for (const prop of dataProps) {
    if (prevProps[prop] !== nextProps[prop]) {
      return false;
    }
  }

  return true;
};

/**
 * Utility hook to detect if we're switching activities
 * Can be used to batch chart updates
 */
export const useActivitySwitchDetection = (axisSeries: any) => {
  const [isActivitySwitching, setIsActivitySwitching] = React.useState(false);
  const prevAxisSeriesRef = React.useRef(axisSeries);
  const isInitialMount = React.useRef(true);

  React.useEffect(() => {
    // Skip detection on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevAxisSeriesRef.current = axisSeries;
      return;
    }

    if (prevAxisSeriesRef.current !== axisSeries) {
      setIsActivitySwitching(true);
      prevAxisSeriesRef.current = axisSeries;

      // Reset the flag after a short delay to allow charts to render
      const timer = setTimeout(() => {
        setIsActivitySwitching(false);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [axisSeries]);

  return isActivitySwitching;
};
